import { ConversationsApi } from '@/rest/conversations-api'
import { InjectionKey } from '@vue/composition-api'
import * as path from 'path'

type MediaElements = Record<
  MediaElement,
  (conversationId: number) => Promise<string>
>

export interface MediaCdn {
  urlFromOwnership(ownership: Ownership, mediaElement: MediaElement): string
  urlFromConversation(
    conversationId: number,
    mediaElement: MediaElement
  ): Promise<string>
  elements: MediaElements
  logo: (clientId: number | string) => string
}

interface MediaCdnConfig {
  host: string
  conversationsApi: ConversationsApi
}

export const mediaElements = [
  'DNA',
  'OperatorWordcloud',
  'CustomerWordcloud',
  'Audio',
  'Video'
] as const
export type MediaElement = typeof mediaElements[number]

export interface Ownership {
  readonly client_id: string
  readonly project_id: string
  readonly team_id: string
  readonly owner_id: string
  readonly conversation_id: string
}

export const ownershipPath = (ownership: Ownership): string => {
  // eslint-disable-next-line
  console.log({ ownership })
  return path.join(
    '/',
    'conversations',
    ownership.client_id,
    ownership.team_id,
    ownership.project_id,
    ownership.owner_id,
    ownership.conversation_id
  )
}

export const mediaElementUri: Record<MediaElement, string> = {
  DNA: 'dna.png',
  OperatorWordcloud: 'wordcloud-operator.png',
  CustomerWordcloud: 'wordcloud-customer.png',
  Audio: 'audio.mp3',
  Video: 'video.mp4'
}

const urlFromOwnership = (config: MediaCdnConfig) => (
  ownership: Ownership,
  mediaElement: MediaElement
): string =>
  `${config.host}${ownershipPath(ownership)}/${mediaElementUri[mediaElement]}`

const urlFromConversation = (config: MediaCdnConfig) => async (
  conversationId: number,
  mediaElement: MediaElement
): Promise<string> => {
  const response = await config.conversationsApi.GetConversation(conversationId)
  if (response._tag === 'success') {
    return urlFromOwnership(config)(
      {
        client_id: response.data?.[0].client_id!,
        project_id: response.data?.[0].project_id!,
        team_id: response.data?.[0].team_id!,
        owner_id: response.data?.[0].owner_id!,
        conversation_id: conversationId.toString()
      },
      mediaElement
    )
  } else {
    // eslint-disable-next-line
    console.error(`Could not load conversation '${conversationId}'`)
    // eslint-disable-next-line
    console.error(response.error)
  }
}

const elements = (
  config: MediaCdnConfig
): Record<MediaElement, (conversationId: number) => Promise<string>> => ({
  DNA: (conversationId: number) =>
    urlFromConversation(config)(conversationId, 'DNA'),
  Audio: (conversationId: number) =>
    urlFromConversation(config)(conversationId, 'Audio'),
  Video: (conversationId: number) =>
    urlFromConversation(config)(conversationId, 'Video'),
  OperatorWordcloud: (conversationId: number) =>
    urlFromConversation(config)(conversationId, 'OperatorWordcloud'),
  CustomerWordcloud: (conversationId: number) =>
    urlFromConversation(config)(conversationId, 'CustomerWordcloud')
})

const logo = (config: MediaCdnConfig) => (clientId: number | string): string =>
  `${config.host}/logos/${clientId}.png`

export const mediaCdn = (config: MediaCdnConfig): MediaCdn => ({
  urlFromOwnership: urlFromOwnership(config),
  urlFromConversation: urlFromConversation(config),
  elements: elements(config),
  logo: logo(config)
})

export const MediaCdnKey: InjectionKey<MediaCdn> = Symbol()
